import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { zohoAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AssignItemsModal from '../components/items/AssignItemsModal';
import { HorizontalScrollTable } from '../components/ui/horizontal-scroll-table';
import { toast } from 'sonner';
import { Loader2, Tags, Search, ArrowRight, Package, UserPlus, AlertTriangle, Download } from 'lucide-react';

export default function CategoriesPage() {
  const { isAdmin, user } = useAuth();
  const isPreviewEnv = user?.app_env === 'preview';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const [productManagers, setProductManagers] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedPM, setSelectedPM] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignBusyCategory, setAssignBusyCategory] = useState(null);

  const [uncatOpen, setUncatOpen] = useState(false);
  const [uncatItems, setUncatItems] = useState([]);
  const [uncatLoading, setUncatLoading] = useState(false);
  const [uncatAssignBusy, setUncatAssignBusy] = useState(null);
  const [uncatCategoryPick, setUncatCategoryPick] = useState({});
  const [uncatExporting, setUncatExporting] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const r = await zohoAPI.getItemCategories(isAdmin ? {} : { mine: true });
      setCategories(r.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchProductManagers = useCallback(async () => {
    try { const r = await itemsAPI.getProductManagers(); setProductManagers((r.data || []).filter(u => u.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || ''))); } catch (e) {}
  }, []);

  const fetchUncategorized = useCallback(async () => {
    setUncatLoading(true);
    try { const r = await zohoAPI.getUncategorizedItems(); setUncatItems(r.data.items || []); }
    catch (e) { toast.error('Failed to load uncategorized items'); }
    finally { setUncatLoading(false); }
  }, []);

  useEffect(() => {
    fetchCategories();
    if (isAdmin) { fetchProductManagers(); fetchUncategorized(); }
  }, [fetchCategories, fetchProductManagers, fetchUncategorized, isAdmin]);

  const filtered = categories.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase()));
  const totalItems = categories.reduce((sum, c) => sum + c.count, 0);

  const handleSelectAllAssignPM = async (categoryName) => {
    setAssignBusyCategory(categoryName);
    try {
      const r = await itemsAPI.list({ category: categoryName, limit: 10000 });
      const skus = (r.data.items || []).map(i => i.sku).filter(Boolean);
      if (skus.length === 0) { toast.error('No items found in this category'); return; }
      setSelectedItems(new Set(skus)); setSelectedPM(''); setAssignModalOpen(true);
    } catch (e) { toast.error('Failed to load items for this category'); }
    finally { setAssignBusyCategory(null); }
  };

  const doAssign = async () => {
    if (!selectedPM || selectedItems.size === 0) { toast.error('Please select a PM'); return; }
    setAssigning(true);
    try {
      const r = await itemsAPI.assign({ item_skus: Array.from(selectedItems), product_manager_id: selectedPM });
      toast.success(r.data.message);
      setAssignModalOpen(false); setSelectedItems(new Set()); setSelectedPM('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign items'); }
    finally { setAssigning(false); }
  };

  const handleAssignCategory = async (zohoItemId) => {
    const categoryName = uncatCategoryPick[zohoItemId];
    if (!categoryName) { toast.error('Pick a category first'); return; }
    setUncatAssignBusy(zohoItemId);
    try {
      const r = await zohoAPI.assignItemCategory(zohoItemId, categoryName);
      toast.success(r.data.message);
      setUncatItems(prev => prev.filter(i => i.zoho_item_id !== zohoItemId));
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign category'); }
    finally { setUncatAssignBusy(null); }
  };

  const handleExportUncategorized = async () => {
    setUncatExporting(true);
    try {
      const r = await zohoAPI.exportUncategorizedItems();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `uncategorized_items_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to export uncategorized items'); }
    finally { setUncatExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Tags className="h-6 w-6 text-primary" />Categories</h1>
        <p className="text-muted-foreground text-sm">
          {isAdmin ? `Item categories synced from Zoho Inventory — ${categories.length} categories, ${totalItems} items` : `Categories with items assigned to you — ${categories.length} categories, ${totalItems} items`}
        </p>
      </div>

      {isAdmin && uncatItems.length > 0 && (
        <Card className="rounded-sm border-amber-200" data-testid="uncategorized-alert">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div>
                <p className="text-sm font-medium">{uncatItems.length} items have no category in Zoho</p>
                <p className="text-xs text-muted-foreground">They'll get missed during category-based PM allocation until assigned.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-sm" disabled={uncatExporting} onClick={handleExportUncategorized} data-testid="export-uncategorized-btn">
                {uncatExporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}Download Excel
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setUncatOpen(true)} data-testid="review-uncategorized-btn">Review Items</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search categories..." className="pl-10 rounded-sm" data-testid="category-search-input" />
      </div>

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <HorizontalScrollTable>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium text-xs uppercase">Category</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Items</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8">
                    <Tags className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">No categories found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((category) => (
                  <TableRow key={category.name} className="table-row-hover" data-testid={`category-row-${category.name}`}>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{category.count}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-sm text-xs"
                            disabled={assignBusyCategory === category.name}
                            onClick={() => handleSelectAllAssignPM(category.name)}
                            data-testid={`select-assign-pm-${category.name}`}
                          >
                            {assignBusyCategory === category.name ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}Select All + Assign PM
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-sm text-xs"
                          onClick={() => navigate(`/items?category=${encodeURIComponent(category.name)}`)}
                          data-testid={`view-items-${category.name}`}
                        >
                          <Package className="h-3.5 w-3.5 mr-1.5" />View Items<ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </HorizontalScrollTable>
        </CardContent>
      </Card>

      {isAdmin && (
        <AssignItemsModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          selectedItems={selectedItems}
          productManagers={productManagers}
          selectedPM={selectedPM}
          setSelectedPM={setSelectedPM}
          onAssign={doAssign}
          assigning={assigning}
        />
      )}

      {isAdmin && (
        <Dialog open={uncatOpen} onOpenChange={setUncatOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="uncategorized-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Uncategorized Items ({uncatItems.length})</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Assigning a category here also updates it in Zoho, so it stays consistent everywhere.</p>
          <div className="flex-1 overflow-y-auto space-y-2">
            {uncatLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : uncatItems.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">All items are categorized</p>
            ) : (
              uncatItems.map(item => (
                <div key={item.zoho_item_id} className="flex items-center gap-2 p-2.5 border rounded-sm" data-testid={`uncat-row-${item.zoho_item_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    {item.sku ? <p className="text-xs text-muted-foreground font-mono">{item.sku}</p> : <p className="text-xs text-amber-600">No SKU in Zoho — set one there first</p>}
                  </div>
                  <Select value={uncatCategoryPick[item.zoho_item_id] || ''} onValueChange={v => setUncatCategoryPick(prev => ({ ...prev, [item.zoho_item_id]: v }))} disabled={!item.sku}>
                    <SelectTrigger className="w-48 rounded-sm shrink-0" data-testid={`uncat-category-select-${item.zoho_item_id}`}><SelectValue placeholder="Pick a category..." /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-sm shrink-0" disabled={!item.sku || uncatAssignBusy === item.zoho_item_id || !uncatCategoryPick[item.zoho_item_id] || isPreviewEnv} title={isPreviewEnv ? 'Preview environment — writing to Zoho is disabled. Use production.' : undefined} onClick={() => handleAssignCategory(item.zoho_item_id)} data-testid={`uncat-assign-btn-${item.zoho_item_id}`}>
                    {uncatAssignBusy === item.zoho_item_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Assign'}
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
